import { defineStore } from 'pinia';
import { useApiStore } from './API.module';

export const useUsersStore = defineStore('users', {
  state: () => ({
    // Права роли — ключи каталога server/lib/permissions.js
    // (docs/Сервер/Права ролей.md). null — ещё не загружены
    permissions: null,
    interfaceConfig: null,
    items: [],
    dealer_list: [],
    supplier_list: [],
    roles_list: [],
  }),
  getters: {
    totalRows: (state) => state.items.length,
    // Это только видимость: данные сервер отдаёт по тем же правам сам
    can: (state) => (key) => Array.isArray(state.permissions) && state.permissions.includes(key),
    canAny: (state) => (...keys) => Array.isArray(state.permissions) && keys.some((k) => state.permissions.includes(k)),
  },
  actions: {
    async getUsers({ partnerId } = {}) {
      try {
        const response = await useApiStore().send({
          method: 'post',
          requestURL: '/users',
          requestData: { partnerId: partnerId || undefined },
        });
        this.items = response || [];
      } catch {
        this.items = [];
      }
    },
    // Новый пользователь: пароль сервер не задаёт, а шлёт ссылку на его
    // установку. Ответ — { id, mailed, message }
    async addUser({ partnerId, name, login, roleId }) {
      return await useApiStore().send({
        method: 'post',
        requestURL: '/users/add',
        requestData: { partnerId, name, login, roleId },
      });
    },
    async updateUser({ partnerUserId, action }) {
      const requests = {
        disable: { method: 'post', requestURL: `/users/${partnerUserId}/block` },
        enable:  { method: 'post', requestURL: `/users/${partnerUserId}/unblock` },
        delete:  { method: 'delete', requestURL: `/users/${partnerUserId}` },
      };
      if (!requests[action]) throw new Error(`Неизвестное действие: ${action}`);
      await useApiStore().send(requests[action]);
    },
    async editUser({ userId, name, login, partnerId, roleId }) {
      await useApiStore().send({
        method: 'put',
        requestURL: '/users/edit',
        requestData: { userId, name, login, partnerId, roleId },
      });
    },
    async sendMail({ partnerUserId }) {
      return await useApiStore().send({
        method: 'post',
        requestURL: `/users/${partnerUserId}/send-mail`,
      });
    },
    // Журнал входов: все попытки входа и выходы. partnerId не задан —
    // сервер возьмёт партнёра текущего пользователя
    async getLoginLog({ partnerId, login, result, from, to, limit, offset } = {}) {
      const query = new URLSearchParams();
      for (const [k, v] of Object.entries({ partnerId, login, result, from, to, limit, offset })) {
        if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
      }
      const qs = query.toString();
      return await useApiStore().send({
        method: 'get',
        requestURL: `/users/login-log${qs ? `?${qs}` : ''}`,
      });
    },
    // Дилеры и поставщики своего дерева — для фильтров «Пользователей».
    // Роли здесь больше не приходят: их список — getRoles
    async getDealerSupplier() {
      try {
        const response = await useApiStore().send({
          method: 'get',
          requestURL: '/users/subpartners',
        });
        const option = v => ({ ...v, value: v.id, text: v.name });
        this.dealer_list = (response?.dealers || []).map(option);
        this.supplier_list = (response?.suppliers || []).map(option);
      } catch {
        this.dealer_list = [];
        this.supplier_list = [];
      }
    },
    async getRoles(partnerId, isDefault = false) {
      try {
        const params = isDefault ? '?default=true' : (partnerId ? `?partnerId=${partnerId}` : '');
        const response = await useApiStore().send({
          method: 'get',
          requestURL: `/roles/list${params}`,
        });
        this.roles_list = (response || []).map(v => ({ ...v, value: v.id, text: v.name }));
      } catch {
        this.roles_list = [];
      }
    },
    // Общий список ролей для «Настройки ролей»: [{ id, name, partnerType,
    // isDefault, people, partners }]; ошибку показывает apiStore
    async fetchSharedRoles() {
      try {
        return await useApiStore().send({ method: 'get', requestURL: '/users/configs/roles' }) || [];
      } catch {
        return [];
      }
    },
    // Баланс своего партнёра — цифра в шапке. null — баланса у партнёра нет
    // (он есть только у дилера)
    async getMyBalance() {
      const data = await useApiStore().send({ method: 'get', requestURL: '/users/me/balance' });
      return data?.balance ?? null;
    },
    async fetchUserConfig() {
      try {
        const response = await useApiStore().send({
          method: 'get',
          requestURL: '/users/configs',
        });
        // Сервер до прав ролей permissions не отдаёт — тогда меню пустое
        this.permissions = Array.isArray(response?.permissions) ? response.permissions : null;
        this.interfaceConfig = response?.interfaceConfig ?? null;
      } catch {
        // ошибка обработана в apiStore
      }
    },
    // Каталог прав для «Настройки ролей»: { groups, permissions: [{ key, title, group, parent }] }
    async fetchPermissionCatalog() {
      return await useApiStore().send({ method: 'get', requestURL: '/users/permissions' });
    },
    async fetchConfigByRoleId(roleId) {
      return await useApiStore().send({
        method: 'get',
        requestURL: `/users/configs/role/${roleId}`,
      });
    },
    // permissions: массив ключей или null — «как у роли по умолчанию»
    // interfaceConfig не передан — поля в теле нет, сервер его не трогает
    async saveRoleConfig({ roleId, permissions, interfaceConfig }) {
      const requestData = { permissions };
      if (interfaceConfig !== undefined) requestData.interfaceConfig = interfaceConfig;
      await useApiStore().send({
        method: 'put',
        requestURL: `/users/configs/role/${roleId}`,
        requestData,
      });
    },
    async revokeRole({ userId }) {
      await useApiStore().send({
        method: 'post',
        requestURL: `/users/${userId}/revoke`,
      });
    },
    async getMyRequests() {
      try {
        return await useApiStore().send({ method: 'get', requestURL: '/users/my-requests' }) || [];
      } catch {
        return [];
      }
    },
    async getJoinRequests() {
      try {
        return await useApiStore().send({ method: 'get', requestURL: '/users/join-requests' }) || [];
      } catch {
        return [];
      }
    },
    async approveJoinRequest({ id, roleId }) {
      await useApiStore().send({
        method: 'post',
        requestURL: `/users/join-requests/${id}/approve`,
        requestData: { roleId },
      });
    },
    async rejectJoinRequest({ id }) {
      await useApiStore().send({
        method: 'post',
        requestURL: `/users/join-requests/${id}/reject`,
      });
    },
  },
});
